import React, { useState, useEffect } from 'react';
import { Window } from '@progress/kendo-react-dialogs';
import { Flex, View } from '@adobe/react-spectrum';
import { useViewInfo } from '../../hooks/UseViewInfo';
import '@progress/kendo-theme-default/dist/all.css';

interface WindowComponentProps {
  id?: string;
  windowIndex?: number;
  visible: boolean;
  title?: React.ReactNode;
  minimizedTitle?: React.ReactNode;
  content?: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;

  contentHeight?: number | string;
  height?: number;
  position?: { top?: number; left?: number };
  zIndex?: number;
  className?: string;
  draggable?: boolean;
  resizable?: boolean;
  showCloseIcon?: boolean;
  showRestoreIcon?: boolean;
  onClose: () => boolean | void;
}

const WindowComponent: React.FC<WindowComponentProps> = ({
  id = 'kendo-window',
  windowIndex = 0,
  visible,
  title = 'Window',
  minimizedTitle = 'Window',
  content,
  footer,
  width = 600,
  height = 'auto',
  contentHeight = 'auto',
  position,
  zIndex,
  className = '',
  draggable = true,
  resizable = true,
  showCloseIcon = true,
  showRestoreIcon = true,
  onClose,
}) => {
  const { isMobile } = useViewInfo();
  const [previousState, setPreviousState] = useState<{
    width: number | string;
    height: number | string;
    top: number;
    left: number;
  } | null>(null);

  const [windowTopPosition] = useState(position?.top ?? 0);
  const [windowLeftPosition] = useState(position?.left ?? 0);

  const [internalVisible, setInternalVisible] = useState(visible);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [windowPosition, setWindowPosition] = useState({ top: windowTopPosition, left: windowLeftPosition });

  const [windowStage, setWindowStage] = useState<'MINIMIZED' | 'FULLSCREEN' | 'DEFAULT' | undefined>('DEFAULT');

  useEffect(() => {
    if (isMinimized) {
      setWindowStage('MINIMIZED');
    } else {
      setWindowStage('DEFAULT');
    }
  }, [isMinimized]);

  // Update internal state when visible prop changes (only for opening)
  useEffect(() => {
    if (visible && !internalVisible) {
      setInternalVisible(true);
    }
  }, [visible]);

  // Window handlers
  const handleClose = () => {
    let action = onClose();
    if (action === false) {
      return;
    }
    setInternalVisible(false);
  };

  const handleMinimize = () => {
    if (!isMinimized) {
      // Store current state
      setPreviousState({
        width: typeof width === 'number' ? width : 600,
        height: typeof height === 'number' ? height : 400,
        top: windowPosition.top,
        left: windowPosition.left,
      });

      // Position at bottom right
      const bottom = window.innerHeight - 45;
      const right = window.innerWidth - 220;
      setWindowPosition({ top: bottom, left: right });
      setIsMinimized(true);
      setIsMaximized(false);
    }
  };

  const handleRestore = () => {
    if (previousState) {
      setWindowPosition({
        top: previousState.top,
        left: previousState.left,
      });
    } else {
      // Default centered position
      const centerTop = window.innerHeight / 3;
      const centerLeft = (window.innerWidth - (typeof width === 'number' ? width : 600)) / 2;
      setWindowPosition({ top: centerTop, left: centerLeft });
    }

    setIsMinimized(false);
    setIsMaximized(false);
  };

  // Custom footer with buttons
  const renderFooter = () => {
    //console.log('footer', footer);
    if (footer) {
      return footer;
    }
    return null;
  };

  // If not visible at all
  if (!internalVisible) {
    return null;
  }

  // Calculate window dimensions
  let normalWidth = undefined;
  if (typeof width === 'number') {
    normalWidth = width;
  }
  const windowWidth = !isMaximized ? normalWidth : undefined;

  let normalHeight = undefined;
  if (typeof height === 'number') {
    normalHeight = height;
  }
  const windowHeight = !isMaximized ? normalHeight : undefined;
  const windowTop = isMaximized ? 0 : windowPosition.top;
  const windowLeft = isMaximized ? 0 : windowPosition.left;

  return (
    <>
      <div
        style={{
          display: isMinimized ? 'flex' : 'none',
          position: 'absolute',
          // Calculate row position (10px for first row, 60px for second row, etc.)
          bottom: `${Math.floor(windowIndex / 5) * 50 + 10}px`,
          // Calculate column position (resets every 5 windows)
          right: `${(windowIndex % 5) * 245 + 10}px`,
          width: '230px',
          height: '40px',
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 10px',
          zIndex,
        }}
        className={`k-window-minimized ${className}`}
      >
        <View UNSAFE_style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{minimizedTitle}</View>
        <Flex gap={'4px'}>
          {showRestoreIcon && (
            <button
              onClick={handleRestore}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: '#666',
              }}
              title="Restore"
            >
              <i className="bi bi-box-arrow-in-up-right"></i>
            </button>
          )}

          {showCloseIcon && (
            <button
              onClick={handleClose}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: '#666',
              }}
              title="Close"
            >
              <i className="bi bi-x" />
            </button>
          )}
        </Flex>
      </div>

      <Window
        stage={windowStage}
        title={title}
        onClose={handleClose}
        initialWidth={windowWidth}
        initialHeight={windowHeight}
        // top={windowTop}
        // left={windowLeft}
        minWidth={windowWidth}
        minHeight={windowHeight}
        draggable={draggable && !isMaximized}
        resizable={resizable && !isMaximized}
        className={className}
        onStageChange={e => {
          //console.log('object', e.state);
          if (e.state === 'MINIMIZED') {
            if (isMobile) return;
            handleMinimize();
          } else if (e.state === 'FULLSCREEN') {
            setWindowStage('FULLSCREEN');
            setIsMaximized(true);
          } else if (e.state === 'DEFAULT') {
            setWindowStage('DEFAULT');
            setIsMaximized(false);
          }

          // if (e.action === 'resize') {
          //   setWindowPosition({
          //     top: e.top,
          //     left: e.left,
          //   });
          // }
        }}
        style={{
          zIndex,
          display: isMinimized ? 'none' : 'block',
          position: 'fixed',
          maxHeight: !isMaximized ? windowHeight : undefined,
        }}
      >
        <div
          style={{
            height: isMaximized ? '600px' : contentHeight || windowHeight,
            maxHeight: !isMaximized ? windowHeight : undefined,
          }}
        >
          {content}
          {renderFooter()}
        </div>
      </Window>
    </>
  );
};

export default WindowComponent;
